import React, { useContext, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { ApiFetch } from '../Common/Api'
import UserContext from '../../UserContext'
import type { ExpiredUnassignedCriteria } from './types'
import { createDefaultExpiredUnassignedCriteria } from './ServiceRequestContext'
import { EXPIRED_UNASSIGNED_INLINE_BREAKPOINT } from './constants'

type ExpiredUnassignedBarProps = {
    criteria: ExpiredUnassignedCriteria
    onCriteriaChange: (criteria: ExpiredUnassignedCriteria) => void
    onRefresh: () => void
    loading?: boolean
}

type FilterOption = {
    value: string
    label: string
}

type ActiveFilterChip = {
    id: string
    label: string
    value: string
}

const normalizeCriteria = (criteria: ExpiredUnassignedCriteria): ExpiredUnassignedCriteria => {
    const defaults = createDefaultExpiredUnassignedCriteria()

    return {
        ...defaults,
        ...criteria,
        authorizationID: criteria.authorizationID?.trim() || undefined,
        memberID: criteria.memberID?.trim() || undefined,
        specialties: [...(criteria.specialties || [])],
        countiesState: [...(criteria.countiesState || [])]
    }
}

const isNarrowExpiredUnassignedViewport = (): boolean =>
    typeof window !== 'undefined' && window.innerWidth <= EXPIRED_UNASSIGNED_INLINE_BREAKPOINT

const ExpiredUnassignedBar: React.FC<ExpiredUnassignedBarProps> = ({
    criteria,
    onCriteriaChange,
    onRefresh,
    loading = false
}) => {
    const { cachedGet } = useContext(UserContext)
    const { CacheLookupsMinutes } = window.CS_CONFIG

    const [stateOptions, setStateOptions] = useState<string[]>([])
    const [serviceTypeOptions, setServiceTypeOptions] = useState<string[]>([])
    const [workingCriteria, setWorkingCriteria] = useState<ExpiredUnassignedCriteria>(() => normalizeCriteria(criteria))
    const [isNarrowActionsLayout, setIsNarrowActionsLayout] = useState<boolean>(() => isNarrowExpiredUnassignedViewport())

    useEffect(() => {
        setWorkingCriteria(normalizeCriteria(criteria))
    }, [criteria])

    useEffect(() => {
        cachedGet('ApiStatesLookup_ExpiredUnassigned', CacheLookupsMinutes, () => ApiFetch<string[]>('County/States', {
            restrictedCounties: false,
            includeAdjacent: true
        }))
            .then((result) => setStateOptions(result || []))
            // eslint-disable-next-line no-console
            .catch(console.log)

        cachedGet('ApiLookupSpecialtyServicesCategories_ExpiredUnassigned', CacheLookupsMinutes, () => ApiFetch<string[]>('Lookup', { key: 'SpecialtyServicesCategories' }))
            .then((result) => setServiceTypeOptions(result || []))
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [cachedGet, CacheLookupsMinutes])

    useEffect(() => {
        const onResize = () => {
            setIsNarrowActionsLayout(isNarrowExpiredUnassignedViewport())
        }

        onResize()
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [])

    const emitCriteria = (nextCriteria: ExpiredUnassignedCriteria) => {
        const normalized = normalizeCriteria(nextCriteria)
        setWorkingCriteria(normalized)
        onCriteriaChange(normalized)
    }

    const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
        const chips: ActiveFilterChip[] = []

        if (workingCriteria.authorizationID) {
            chips.push({
                id: 'authorizationID',
                label: 'ID',
                value: workingCriteria.authorizationID
            })
        }

        if (workingCriteria.memberID) {
            chips.push({
                id: 'memberID',
                label: 'Member',
                value: workingCriteria.memberID
            })
        }

        workingCriteria.specialties.forEach((specialty) => {
            chips.push({
                id: 'specialty:' + String(specialty),
                label: 'Service Type',
                value: specialty
            })
        })

        workingCriteria.countiesState.forEach((state) => {
            chips.push({
                id: 'state:' + String(state),
                label: 'State',
                value: state
            })
        })

        return chips
    }, [workingCriteria])

    const mappedStateOptions = useMemo<FilterOption[]>(() => stateOptions.map((state) => ({ value: state, label: state })), [stateOptions])
    const mappedServiceTypeOptions = useMemo<FilterOption[]>(() => serviceTypeOptions.map((serviceType) => ({ value: serviceType, label: serviceType })), [serviceTypeOptions])

    const clearAll = () => {
        const defaults = createDefaultExpiredUnassignedCriteria()
        emitCriteria(defaults)
    }

    const removeChip = (chipId: string) => {
        if (chipId === 'authorizationID') {
            emitCriteria({ ...workingCriteria, authorizationID: undefined })
            return
        }

        if (chipId === 'memberID') {
            emitCriteria({
                ...workingCriteria,
                memberID: undefined
            })
            return
        }

        if (chipId.startsWith('specialty:')) {
            const specialty = chipId.replace('specialty:', '')
            emitCriteria({
                ...workingCriteria,
                specialties: workingCriteria.specialties.filter((value) => value !== specialty)
            })
            return
        }

        if (chipId.startsWith('state:')) {
            const state = chipId.replace('state:', '')
            emitCriteria({
                ...workingCriteria,
                countiesState: workingCriteria.countiesState.filter((value) => value !== state)
            })
        }
    }

    return (
        <div className='expired-unassigned-bar'>
            <div className='expired-unassigned-row'>
                <div className='expired-unassigned-field expired-unassigned-field-id'>
                    <label htmlFor='expired-unassigned-authorization' className='expired-unassigned-label'>ID</label>
                    <input
                        id='expired-unassigned-authorization'
                        type='text'
                        className='expired-unassigned-text'
                        placeholder='Authorization ID'
                        value={workingCriteria.authorizationID || ''}
                        onChange={(event) => emitCriteria({ ...workingCriteria, authorizationID: event.target.value })}
                        disabled={loading}
                    />
                </div>

                <div className='expired-unassigned-field expired-unassigned-field-member'>
                    <label htmlFor='expired-unassigned-member-id' className='expired-unassigned-label'>Member ID</label>
                    <input
                        id='expired-unassigned-member-id'
                        type='text'
                        className='expired-unassigned-text'
                        placeholder='Member ID'
                        value={workingCriteria.memberID || ''}
                        onChange={(event) => {
                            emitCriteria({
                                ...workingCriteria,
                                memberID: event.target.value
                            })
                        }}
                        disabled={loading}
                    />
                </div>

                <div className='expired-unassigned-field expired-unassigned-field-service-type'>
                    <label htmlFor='expired-unassigned-service-type' className='expired-unassigned-label'>Service Type</label>
                    <Select<FilterOption, true>
                        inputId='expired-unassigned-service-type'
                        isMulti
                        closeMenuOnSelect={false}
                        className='expired-unassigned-react-select'
                        classNamePrefix='react-select'
                        placeholder='All service types'
                        options={mappedServiceTypeOptions}
                        value={workingCriteria.specialties.map((value) => ({ value, label: value }))}
                        onChange={(selectedOptions) => {
                            const options = (selectedOptions || []) as FilterOption[]
                            emitCriteria({
                                ...workingCriteria,
                                specialties: options.map((option) => option.value)
                            })
                        }}
                        isDisabled={loading}
                    />
                </div>

                <div className='expired-unassigned-field expired-unassigned-field-state'>
                    <label htmlFor='expired-unassigned-state' className='expired-unassigned-label'>State</label>
                    <Select<FilterOption, true>
                        inputId='expired-unassigned-state'
                        isMulti
                        closeMenuOnSelect={false}
                        className='expired-unassigned-react-select'
                        classNamePrefix='react-select'
                        placeholder='All states'
                        options={mappedStateOptions}
                        value={workingCriteria.countiesState.map((value) => ({ value, label: value }))}
                        onChange={(selectedOptions) => {
                            const options = (selectedOptions || []) as FilterOption[]
                            emitCriteria({
                                ...workingCriteria,
                                countiesState: options.map((option) => option.value)
                            })
                        }}
                        isDisabled={loading}
                    />
                </div>

                <div
                    className={`expired-unassigned-actions ${isNarrowActionsLayout ? 'expired-unassigned-actions-wrapped' : 'expired-unassigned-actions-inline'}`}
                    data-layout={isNarrowActionsLayout ? 'wrapped' : 'inline'}
                    data-testid='expired-unassigned-actions'
                >
                    <button
                        type='button'
                        className='button small expired-unassigned-button'
                        onClick={onRefresh}
                        disabled={loading}
                    >
                        Refresh
                    </button>
                    <button
                        type='button'
                        className='button clear small expired-unassigned-button'
                        onClick={clearAll}
                        disabled={loading}
                    >
                        Clear
                    </button>
                </div>
            </div>

            <div className='expired-unassigned-active-filters'>
                <span className='active-filters-label'>ACTIVE FILTERS:</span>
                {activeFilterChips.map((chip) => (
                    <button
                        key={chip.id}
                        type='button'
                        className='expired-unassigned-filter-chip'
                        onClick={() => removeChip(chip.id)}
                        disabled={loading}
                    >
                        {chip.label}: {chip.value}
                        <span className='expired-unassigned-filter-chip-close'>x</span>
                    </button>
                ))}

                {activeFilterChips.length > 0 && (
                    <button
                        type='button'
                        className='expired-unassigned-filter-clear-all'
                        onClick={clearAll}
                        disabled={loading}
                    >
                        Clear all
                    </button>
                )}
            </div>
        </div>
    )
}

export default ExpiredUnassignedBar
